import React, { useState, useEffect } from "react";
import { NavLink as RouterNavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";
import { sidebarLink } from "../../types/sidebarLink";


type Props = {
  link: sidebarLink;
  isCollapsed?: boolean;
  isHovered?: boolean;
  isPinned?: boolean;
  onLinkClick?: () => void;
};

export default function NavLink({ link, isCollapsed, isHovered, isPinned, onLinkClick }: Props) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const hasChildren = link.children && link.children.length > 0;

  const isAnyChildActive = React.useMemo(() => {
    return !!(hasChildren && link.children?.some(child =>
      child.href && (pathname === child.href || pathname.startsWith(child.href + "/"))
    ));
  }, [hasChildren, link.children, pathname]);

  const [open, setOpen] = useState(isAnyChildActive || false);
  const isLinkActive = link.href
    ? pathname === link.href || (hasChildren && pathname.startsWith(`${link.href}/`))
    : isAnyChildActive;

  useEffect(() => {
    if (isAnyChildActive) {
      setOpen(true);
    }
  }, [isAnyChildActive]);

  const handleParentClick = (event?: React.MouseEvent<HTMLElement>) => {
    if (hasChildren) {
      event?.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  const handleChildClick = () => {
  };

  return (
    <div>
      {link.href ? (
        <RouterNavLink
          to={link.href}
          end={!hasChildren}
          className={({ isActive }) =>
            `flex items-center justify-between rounded-xl px-3 py-2.5 transition-all duration-200 font-medium cursor-pointer ${isActive
              ? "bg-emerald-50 text-emerald-700 shadow-sm ring-1 ring-emerald-100"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`
          }
          onClick={(event) => handleParentClick(event)}
        >
          <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${isLinkActive ? "bg-white text-emerald-600" : "bg-slate-50"}`}>
              <link.icon size={18} />
            </span>
            {(!isCollapsed || isHovered || isPinned) && (
              <span className="text-sm capitalize">{t(link.title as any)}</span>
            )}
            {link.badge && link.badge > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 ml-1 text-xs font-bold text-white bg-red-500 rounded-full min-w-[20px] flex-shrink-0">
                {link.badge > 99 ? "99+" : link.badge}
              </span>
            )}
          </div>

          {(!isCollapsed || isHovered || isPinned) && hasChildren && (
            <span>
              {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </span>
          )}
        </RouterNavLink>
      ) : (
        <div
          className={`flex items-center justify-between rounded-xl px-3 py-2.5 cursor-pointer font-medium transition-all duration-200 ${isLinkActive
            ? "bg-emerald-50 text-emerald-700 shadow-sm ring-1 ring-emerald-100"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          onClick={(event) => handleParentClick(event)}
        >
          <div className="flex items-center gap-3 font-medium">
            <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${isLinkActive ? "bg-white text-emerald-600" : "bg-slate-50"}`}>
              <link.icon size={18} />
            </span>
            {(!isCollapsed || isHovered || isPinned) && (
              <span className="text-sm capitalize">{t(link.title as any)}</span>
            )}
            {link.badge && link.badge > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 ml-1 text-xs font-bold text-white bg-red-500 rounded-full min-w-[20px] flex-shrink-0">
                {link.badge > 99 ? "99+" : link.badge}
              </span>
            )}
          </div>
          {(!isCollapsed || isHovered || isPinned) && hasChildren && (
            <span>
              {open ? <ChevronDown size={18} /> : i18n.language === "ar" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </span>
          )}
        </div>
      )}

      {hasChildren && open && (!isCollapsed || isHovered || isPinned) && (
        <div className="pl-6 mt-1">
          {link.children!.map((child) => (
            <RouterNavLink
              key={child.title}
              to={child.href!}
              onClick={handleChildClick}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-lg px-3 py-2 mb-1 text-sm font-medium transition duration-200 ${isActive
                  ? "bg-emerald-50 text-emerald-700"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                }`
              }
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-50">
                <child.icon size={16} />
              </span>
              <span className="capitalize">{t(child.title as any)}</span>
            </RouterNavLink>
          ))}
        </div>
      )}
    </div>
  );
}
